import { useRef } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, X } from "lucide-react";
import { AttachmentsPanel } from "@/components/shared/AttachmentsPanel";
import { usePasteImages } from "@/lib/usePasteImages";

const OK = /\.(pdf|docx?|xlsx?|pptx?|csv|txt|png|jpe?g|gif|webp)$/i;
const MAX = 10 * 1024 * 1024;

export async function uploadPending(entityType, entityId, files) {
  for (const f of files) {
    const fd = new FormData();
    fd.append("entity_type", entityType);
    fd.append("entity_id", entityId);
    fd.append("file", f);
    fd.append("name", f.name);
    try { await client.post("/attachments", fd); } catch (e) { toast.error(`${f.name}: ${e?.response?.data?.detail || "upload failed"}`); }
  }
}

function PendingPicker({ files, setFiles }) {
  const ref = useRef(null);
  const add = (list) => {
    const ok = [...list].filter((f) => {
      if (!OK.test(f.name)) { toast.error(`${f.name}: file type not supported`); return false; }
      if (f.size > MAX) { toast.error(`${f.name} exceeds the 10 MB limit`); return false; }
      return true;
    });
    setFiles([...files, ...ok]);
  };
  const pasteRef = usePasteImages(add);
  return (
    <div ref={pasteRef} className="mt-4 space-y-2 rounded-lg border border-dashed border-slate-200 p-3" data-testid="pending-attachments">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Paperclip className="h-3.5 w-3.5" />Attachments</p>
        <button type="button" onClick={() => ref.current?.click()} className="text-xs font-semibold text-sky-700 hover:underline" data-testid="pending-attachments-add">Add files</button>
        <input ref={ref} type="file" multiple hidden onChange={(e) => { add(e.target.files || []); e.target.value = ""; }} data-testid="pending-attachments-input" />
      </div>
      {files.length ? files.map((f, i) => (
        <div key={`${f.name}-${i}`} className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-700" data-testid={`pending-attachment-${i}`}>
          <span className="truncate">{f.name}</span>
          <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
        </div>
      )) : <p className="text-[11px] text-slate-400">Files will upload when you save. You can also paste an image (Ctrl+V).</p>}
    </div>
  );
}

export function ActionAttachments({ recordId, files, setFiles }) {
  if (recordId) return <AttachmentsPanel entityType="action" entityId={recordId} simpleNaming />;
  return <PendingPicker files={files} setFiles={setFiles} />;
}
